import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  Pagination,
  Grid
} from '@mui/material';
import { Settings as SettingsIcon, Download as DownloadIcon } from '@mui/icons-material';
import { getApiUrl } from '../utils/httpClient';

interface ResearchProps {}

const Research: React.FC<ResearchProps> = () => {
  const [researchData, setResearchData] = React.useState<any>(null);
  const [activeResearchTab, setActiveResearchTab] = React.useState('compute');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string>('');
  const [computePage, setComputePage] = React.useState(1);
  const [servicesPage, setServicesPage] = React.useState(1);
  const itemsPerPage = 10;

  // Download JSON data function
  const downloadJsonData = () => {
    const combinedData = {
      metadata: {
        exported_at: new Date().toISOString(),
        app_version: "3.7.1",
        description: "Alfred Cloud Compare - AWS to OCI Service Mappings and Pricing Data"
      },
      research_data: researchData
    };
    
    const dataStr = JSON.stringify(combinedData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `alfred-cloud-compare-data-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Fetch research data
  React.useEffect(() => {
    const fetchResearchData = async () => {
      try {
        const apiUrl = getApiUrl('/sku-research');
        
        const skuRes = await fetch(apiUrl);
        
        if (!skuRes.ok) {
          throw new Error(`HTTP error! SKU: ${skuRes.status}`);
        }
        
        const skuData = await skuRes.json();
        
        setResearchData(skuData.research_data || skuData);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch research data:', error);
        setError(error instanceof Error ? error.message : 'Unknown error occurred');
        setLoading(false);
      }
    };
    fetchResearchData();
  }, []);

  if (loading) {
    return (
      <Card sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading Research Data...
        </Typography>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load research data: {error}
        <br />
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
          Make sure the backend is running on port 8001
        </Typography>
      </Alert>
    );
  }

  if (!researchData) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        No research data available
      </Alert>
    );
  }

  return (
    <Box>
      <Card elevation={2} sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h5" component="h2" sx={{ color: 'grey.900', fontWeight: 600, mb: 1 }}>
            AWS to OCI Research & Mapping Data
          </Typography>
          {researchData?.metadata && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="grey.600" sx={{ display: 'block' }}>
                <strong>Last Updated:</strong> {new Date(researchData.metadata.last_updated).toLocaleDateString()} | 
                <strong> Pricing Date:</strong> {researchData.metadata.pricing_date} | 
                <strong> Version:</strong> {researchData.metadata.version}
              </Typography>
              <Typography variant="caption" color="grey.600" sx={{ display: 'block' }}>
                <strong>Regions:</strong> {researchData.metadata.regions_compared?.join(', ')} | 
                <strong> Update Schedule:</strong> {researchData.metadata.update_schedule}
              </Typography>
            </Box>
          )}
        </Box>
        
        <Tabs 
          value={activeResearchTab} 
          onChange={(e, newValue) => setActiveResearchTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            '& .MuiTabs-indicator': { height: 3 },
            '& .MuiTab-root': { 
              minHeight: 44,
              fontWeight: 500,
              fontSize: '0.85rem',
              minWidth: 80,
              padding: '6px 12px'
            }
          }}
        >
          <Tab label="Compute" value="compute" />
          <Tab label="Container Services" value="container-services" />
          <Tab label="ML/AI Services" value="ml-ai-services" />
          <Tab label="Storage" value="storage" />
          <Tab label="Networking" value="networking" />
          <Tab label="Security & Identity" value="security-identity" />
          <Tab label="Other Services" value="other-services" />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <SettingsIcon sx={{ fontSize: '1rem' }} />
                Data
              </Box>
            } 
            value="data" 
          />
        </Tabs>
      </Card>

      {/* Compute Infrastructure */}
      {activeResearchTab === 'compute' && (
        <Box>
          {/* Summary Card */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                EC2 to OCI Instance Mappings
                <Chip label={`${Object.values(researchData.ec2_to_oci_shape_mappings || {}).reduce((total: number, category: any) => total + (category ? Object.keys(category).length : 0), 0)} Instances`} size="small" color="primary" variant="outlined" />
              </Typography>
              
              <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.dark', mb: 1 }}>
                  🚀 v1.4 Expansion: Latest Generation Instances
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Comprehensive AWS to OCI mappings including latest generations: C7i, R7i, M6i/M6a, X1e/X2i, I4i series.
                  All instances mapped to optimal OCI shapes with performance and cost details.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Instance SKU</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>CPU</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Memory</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Network</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Hourly Cost</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Monthly Cost</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {researchData.enterprise_compute_families?.general_purpose?.aws_m_series?.map((awsInstance: any, index: number) => {
                      const ociInstance = researchData.enterprise_compute_families?.general_purpose?.oci_standard_series?.[index];
                      const savingsPercent = ociInstance ? Math.round(((awsInstance.price_per_hour - ociInstance.price_per_hour) / awsInstance.price_per_hour) * 100) : 0;
                      
                      return (
                        <React.Fragment key={`mapping-${index}`}>
                          {/* AWS Row */}
                          <TableRow hover sx={{ 
                            bgcolor: 'warning.50', 
                            '&:hover': { bgcolor: 'warning.100' },
                            borderBottom: ociInstance ? 'none' : '1px solid rgba(224, 224, 224, 1)'
                          }}>
                            <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                              <Chip label="AWS" color="warning" size="small" />
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                              <strong>{awsInstance.sku}</strong>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                              {awsInstance.vcpu} vCPU
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                              {awsInstance.memory_gb} GB
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                              {awsInstance.network_gbps} Gbps
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                              ${awsInstance.price_per_hour}/hr
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                              ${awsInstance.price_per_month}/mo
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem', mr: 0.5 }}>
                                  ↓
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                          
                          {/* OCI Row */}
                          {ociInstance ? (
                            <TableRow hover sx={{ 
                              bgcolor: 'success.50', 
                              '&:hover': { bgcolor: 'success.100' },
                              borderBottom: '2px solid rgba(25, 118, 210, 0.2)'
                            }}>
                              <TableCell sx={{ py: 0.5 }}>
                                <Chip label="OCI" color="success" size="small" />
                              </TableCell>
                              <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                                <strong>{ociInstance.sku}</strong>
                              </TableCell>
                              <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                                {ociInstance.ocpu} OCPU
                              </TableCell>
                              <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                                {ociInstance.memory_gb} GB
                              </TableCell>
                              <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                                {ociInstance.network_gbps} Gbps
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                                ${ociInstance.price_per_hour}/hr
                              </TableCell>
                              <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                                ${ociInstance.price_per_month || (ociInstance.price_per_hour * 730).toFixed(2)}/mo
                              </TableCell>
                              <TableCell align="center" sx={{ py: 0.5 }}>
                                {savingsPercent > 0 ? (
                                  <Chip 
                                    label={`${savingsPercent}%`} 
                                    size="small" 
                                    color="success" 
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ) : (
                                  <Chip 
                                    label={`${Math.abs(savingsPercent)}%`} 
                                    size="small" 
                                    color="error" 
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          ) : (
                            <TableRow sx={{ bgcolor: 'grey.100', borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                              <TableCell colSpan={7} align="center" sx={{ py: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                                No direct OCI equivalent - Contact Oracle for custom mapping
                              </TableCell>
                              <TableCell align="center" sx={{ py: 0.5 }}>
                                <Chip label="N/A" size="small" color="default" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Compute Optimized Instances */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem' }}>Compute Optimized Instances</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Instance SKU</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>CPU</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Memory</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Network</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Hourly Cost</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Use Case</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {researchData.enterprise_compute_families?.compute_optimized?.aws_c_series?.map((instance: any, index: number) => (
                      <TableRow key={`aws-c-${index}`} hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' } }}>
                        <TableCell sx={{ py: 0.5 }}><Chip label="AWS" color="warning" size="small" /></TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}><strong>{instance.sku}</strong></TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>{instance.vcpu} vCPU</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>{instance.memory_gb} GB</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>{instance.network_gbps} Gbps</TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>${instance.price_per_hour}/hr</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.7rem' }}>{instance.use_case}</TableCell>
                      </TableRow>
                    ))}
                    {researchData.enterprise_compute_families?.compute_optimized?.oci_optimized_series?.map((instance: any, index: number) => (
                      <TableRow key={`oci-c-${index}`} hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' } }}>
                        <TableCell sx={{ py: 0.5 }}><Chip label="OCI" color="success" size="small" /></TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}><strong>{instance.sku}</strong></TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>{instance.ocpu} OCPU</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>{instance.memory_gb} GB</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>{instance.network_gbps} Gbps</TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>${instance.price_per_hour}/hr</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.7rem' }}>{instance.use_case}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Memory Optimized Instances */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem' }}>Memory Optimized Instances</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Instance SKU</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>CPU</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Memory</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Network</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Hourly Cost</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1 }}>Cost Advantage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {researchData.enterprise_compute_families?.memory_optimized?.aws_r_series?.map((instance: any, index: number) => (
                      <TableRow key={`aws-r-${index}`} hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' } }}>
                        <TableCell sx={{ py: 0.5 }}><Chip label="AWS" color="warning" size="small" /></TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}><strong>{instance.sku}</strong></TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>{instance.vcpu} vCPU</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>{instance.memory_gb} GB</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>{instance.network_gbps} Gbps</TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>${instance.price_per_hour}/hr</TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.7rem' }}>-</TableCell>
                      </TableRow>
                    ))}
                    {researchData.enterprise_compute_families?.memory_optimized?.oci_memory_series?.map((instance: any, index: number) => (
                      <TableRow key={`oci-r-${index}`} hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' } }}>
                        <TableCell sx={{ py: 0.5 }}><Chip label="OCI" color="success" size="small" /></TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}><strong>{instance.sku}</strong></TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>{instance.ocpu} OCPU</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>{instance.memory_gb} GB</TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>{instance.network_gbps} Gbps</TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>${instance.price_per_hour}/hr</TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.7rem', color: 'success.main' }}>{instance.cost_advantage}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Bare Metal Instances */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                Bare Metal Instances
                <Chip label="Direct Hardware Access" size="small" color="secondary" variant="outlined" />
              </Typography>
              
              {/* Bare Metal Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'secondary.50', borderRadius: 1, border: '1px solid', borderColor: 'secondary.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.dark', mb: 1 }}>
                  🖥️ Bare Metal Computing
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  AWS bare metal instances (*.metal) provide direct hardware access without hypervisor overhead.
                  OCI bare metal (BM.*) instances offer similar dedicated hardware performance with competitive pricing.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Instance SKU</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>CPU</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Memory</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Network</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Hourly Cost</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Monthly Cost</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* AWS Bare Metal Instances */}
                    <React.Fragment key="aws-metal-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>m7i.metal</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          128 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          512 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          50 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $6.144/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $4,485/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>BM.Standard.E5.192</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          96 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1440 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          50 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $4.915/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $3,588/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="20%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    <React.Fragment key="aws-metal-2">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>c6i.metal</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          128 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          256 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          50 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $6.528/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $4,765/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>BM.Optimized3.36</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          36 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          512 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          40 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $2.275/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $1,661/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="65%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* More Comprehensive Bare Metal Instances */}
                    {/* r6i.metal - Memory Optimized */}
                    <React.Fragment key="aws-metal-r6i">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>r6i.metal</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          128 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1024 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          50 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $8.064/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $5,887/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>BM.Standard.E4.128</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          64 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1024 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          50 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $6.144/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $4,485/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="24%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* x2iezn.metal - High Memory */}
                    <React.Fragment key="aws-metal-x2iezn">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>x2iezn.metal</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          128 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          3840 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          100 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $13.344/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $9,741/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>BM.HighMemory2.416</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          208 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          6656 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          100 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $9.984/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $7,289/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="25%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* m6i.metal - General Purpose */}
                    <React.Fragment key="aws-metal-m6i">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>m6i.metal</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          128 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          512 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          50 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $4.608/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $3,364/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>BM.Standard3.64</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          64 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          512 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          50 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $3.072/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $2,243/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="33%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* c6i.metal - Compute Optimized */}
                    <React.Fragment key="aws-metal-c6i">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>c6i.metal</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          128 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          256 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          50 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $3.456/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $2,523/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>BM.Standard.E4.128</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          64 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1024 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          50 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $2.688/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $1,962/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="22%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* i4i.metal - Storage Optimized */}
                    <React.Fragment key="aws-metal-i4i">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>i4i.metal</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          128 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1024 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          75 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $6.912/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $5,046/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>BM.DenseIO2.52</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          52 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          768 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          50 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $4.992/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $3,644/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="28%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* x2gd.metal - ARM-based */}
                    <React.Fragment key="aws-metal-x2gd">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>x2gd.metal</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          64 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1024 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          25 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $5.424/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $3,960/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>BM.Standard.A1.160</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          80 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          512 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          40 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $3.840/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $2,803/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="29%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Micro & Lightsail Instances */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                Micro & Lightsail Instances
                <Chip label="Always Free • Entry Level" size="small" color="success" variant="outlined" />
              </Typography>
              
              {/* Micro Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark', mb: 1 }}>
                  💰 Entry-Level & Always Free Instances
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Small instances perfect for development, testing, and light workloads. OCI's Always Free tier provides significant cost advantages for these use cases.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Instance SKU</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>CPU</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Memory</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Network</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Hourly Cost</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Monthly Cost</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* t2.nano - Micro Instance */}
                    <React.Fragment key="micro-t2nano">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>t2.nano</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          0.5 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Low
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.0058/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $4.23/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>VM.Standard.A1.Flex</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          <strong>FREE</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          <strong>FREE</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="100%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* t2.micro - Classic Micro */}
                    <React.Fragment key="micro-t2micro">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>t2.micro</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Low to Moderate
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.0116/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $8.47/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>VM.Standard.E2.1.Micro</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          <strong>FREE</strong>  
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          <strong>FREE</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="100%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* t3.micro - Burstable */}
                    <React.Fragment key="micro-t3micro">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>t3.micro</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          2 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Up to 5 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.0104/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $7.59/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>VM.Standard.A1.Flex</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          2 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          2 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          2 Gbps
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          <strong>FREE</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          <strong>FREE</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="100%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Lightsail $3.50 Plan */}
                    <React.Fragment key="lightsail-small">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Lightsail $3.50</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          512 MB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1 TB Transfer
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.0048/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $3.50/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>VM.Standard.A1.Flex</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          10 TB Transfer
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          <strong>FREE</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          <strong>FREE</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="100%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Lightsail $5 Plan */}
                    <React.Fragment key="lightsail-medium">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Lightsail $5</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          2 TB Transfer
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.0068/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $5.00/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>VM.Standard.A1.Flex</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          2 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          10 TB Transfer
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          <strong>FREE</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          <strong>FREE</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="100%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Lightsail $10 Plan */}
                    <React.Fragment key="lightsail-large">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Lightsail $10</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1 vCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          2 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          3 TB Transfer
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.0137/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $10.00/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>VM.Standard.E2.1</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1 OCPU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          8 GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          10 TB Transfer
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.0094/hr
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $6.86/mo
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="31%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Container Services */}
      {activeResearchTab === 'container-services' && (
        <Box>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                Container Services Mapping
                <Chip label={`${Object.keys(researchData.aws_to_oci_service_mappings?.container_services || {}).length} Services`} size="small" color="primary" variant="outlined" />
              </Typography>
              
              <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.dark', mb: 1 }}>
                  🚀 v1.4 Expansion: Comprehensive Container Services
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete AWS container services mapped to OCI equivalents: ECS, EKS, ECR, Fargate, App Runner and more.
                  All services mapped with confidence scores, pricing models, and migration notes.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Service Name</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '18%' }}>Product</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Pricing Model</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Cost Comparison</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '13%' }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(researchData.aws_to_oci_service_mappings?.container_services || {}).map(([awsService, mapping]: [string, any]) => (
                      <React.Fragment key={awsService}>
                        <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                          <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                            <Chip label="AWS" color="warning" size="small" />
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                            <strong>{awsService}</strong>
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                            {awsService.includes('ECS') ? 'Container Orchestration' : 
                             awsService.includes('EKS') ? 'Kubernetes Service' :
                             awsService.includes('ECR') ? 'Container Registry' :
                             awsService.includes('Fargate') ? 'Serverless Containers' :
                             awsService.includes('App Runner') ? 'Container Deployment' :
                             'Container Service'}
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                            {awsService.includes('Fargate') ? 'Serverless' : 
                             awsService.includes('Registry') || awsService.includes('ECR') ? 'Registry' : 
                             'Managed'}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                            {awsService.includes('ECS') ? 'Per task/hour' : 
                             awsService.includes('EKS') ? 'Per cluster/hour' :
                             awsService.includes('ECR') ? 'Per GB/month' :
                             awsService.includes('Fargate') ? 'Per vCPU/GB/sec' :
                             awsService.includes('App Runner') ? 'Per vCPU/GB/hour' :
                             'Usage-based'}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                            <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary', borderBottom: 'none' }}>
                            AWS container service
                          </TableCell>
                        </TableRow>
                        <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                          <TableCell sx={{ py: 0.5 }}>
                            <Chip label="OCI" color="success" size="small" />
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                            <strong>{mapping.oci_service}</strong>
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            {mapping.oci_product}
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            {mapping.oci_product.includes('Virtual Nodes') ? 'Serverless' :
                             mapping.oci_product.includes('Registry') ? 'Registry' :
                             'Managed'}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            {mapping.oci_product.includes('Virtual Nodes') ? 'Per vCPU/GB/sec' :
                             mapping.oci_product.includes('Registry') ? 'Per GB/month' :
                             mapping.oci_service.includes('Kubernetes') ? 'Per worker/hour' :
                             'Usage-based'}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500, textAlign: 'center', display: 'block' }}>
                              {mapping.cost_comparison || `${Math.round((mapping.mapping_confidence || 0) * 100)}%`}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                            {mapping.notes}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* AI Infrastructure */}
      {activeResearchTab === 'ai-infrastructure' && (
        <Box>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                AI Infrastructure
                <Chip label="GPUs & AI Accelerators" size="small" color="info" variant="outlined" />
              </Typography>
              
              {/* AI Infrastructure Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.dark', mb: 1 }}>
                  🤖 AI & Machine Learning Hardware
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Comparison of GPU instances, AI accelerators, and specialized chips for machine learning training and inference workloads.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '18%' }}>Instance SKU</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>GPU Type</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>GPUs</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Memory</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Hourly Cost</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '16%' }}>Use Case</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* AWS GPU Instances */}
                    <React.Fragment key="aws-gpu-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>p4d.24xlarge</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          A100 40GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          8
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1152 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $32.77/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          ML Training
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>BM.GPU4.8</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          A100 40GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          8
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1024 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $22.95/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          ML Training
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="30%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    <React.Fragment key="aws-gpu-2">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>g5.24xlarge</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          A10G 24GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          4
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          384 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $5.67/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          ML Inference
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>VM.GPU.A10.4</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          A10 24GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          4
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          240 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $3.20/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          ML Inference
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="44%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* H100 Instances */}
                    <React.Fragment key="h100-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>p5.48xlarge</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          H100 80GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          8
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          2048 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $98.32/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Large Language Models
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>BM.GPU.H100.8</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          H100 80GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          8
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          2048 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $75.00/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          LLM Training/Inference
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="24%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* V100 Training Instances */}
                    <React.Fragment key="v100-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>p3.8xlarge</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          V100 16GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          4
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          244 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $12.24/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          ML Training
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>BM.GPU3.8</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          V100 16GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          8
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          768 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $10.80/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          ML Training
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="12%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* T4 Inference Instances */}
                    <React.Fragment key="t4-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>g4dn.xlarge</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          T4 16GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          16 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.526/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          ML Inference
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>VM.GPU2.1</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          P100 16GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          1
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          13 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.395/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          ML Inference
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="25%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Large T4 Multi-GPU */}
                    <React.Fragment key="t4-multi-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>g4dn.12xlarge</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          T4 16GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          4
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          192 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $3.912/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Batch Inference
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>BM.GPU2.2</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          P100 16GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          2
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          256 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $2.70/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Batch Processing
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="31%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* AWS Inferentia */}
                    <React.Fragment key="inferentia-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>inf1.xlarge</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Inferentia
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          1
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          8 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.228/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Cost-Optimized Inference
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>VM.Standard.E4.Flex</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          CPU Optimized
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          0
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          8 GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.168/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          CPU Inference
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="26%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* ML/AI Services */}
      {activeResearchTab === 'ml-ai-services' && (
        <Box>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                ML/AI Services Mapping
                <Chip label={`${Object.keys(researchData.aws_to_oci_service_mappings?.ml_ai_services || {}).length} Services`} size="small" color="primary" variant="outlined" />
              </Typography>
              
              <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.dark', mb: 1 }}>
                  🤖 v1.4 Expansion: Comprehensive ML/AI Services
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete AWS ML/AI services mapped to OCI equivalents: SageMaker, Bedrock, Rekognition, Textract, Comprehend and more.
                  All services mapped with confidence scores, pricing models, and migration guidance.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Service Name</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '18%' }}>Product</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Pricing Model</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Confidence</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '13%' }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(researchData.aws_to_oci_service_mappings?.ml_ai_services || {}).map(([awsService, mapping]: [string, any]) => (
                      <React.Fragment key={awsService}>
                        <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                          <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                            <Chip label="AWS" color="warning" size="small" />
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                            <strong>{awsService}</strong>
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                            {awsService.includes('SageMaker') ? 'ML Platform' : 
                             awsService.includes('Bedrock') ? 'Foundation Models' :
                             awsService.includes('Rekognition') ? 'Computer Vision' :
                             awsService.includes('Textract') ? 'Document AI' :
                             awsService.includes('Comprehend') ? 'NLP Service' :
                             awsService.includes('Polly') ? 'Text-to-Speech' :
                             awsService.includes('Transcribe') ? 'Speech-to-Text' :
                             awsService.includes('Translate') ? 'Translation' :
                             'AI/ML Service'}
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                            {awsService.includes('SageMaker') ? 'Platform' : 
                             awsService.includes('Bedrock') ? 'Models' :
                             awsService.includes('Rekognition') || awsService.includes('Textract') || awsService.includes('Comprehend') ? 'API' :
                             'Service'}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                            {awsService.includes('SageMaker') ? 'Per instance/hour' : 
                             awsService.includes('Bedrock') ? 'Per token' :
                             awsService.includes('Rekognition') || awsService.includes('Textract') ? 'Per image/page' :
                             awsService.includes('Comprehend') || awsService.includes('Translate') ? 'Per character' :
                             awsService.includes('Polly') || awsService.includes('Transcribe') ? 'Per minute' :
                             'Usage-based'}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                            <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary', borderBottom: 'none' }}>
                            AWS AI/ML service
                          </TableCell>
                        </TableRow>
                        <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                          <TableCell sx={{ py: 0.5 }}>
                            <Chip label="OCI" color="success" size="small" />
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                            <strong>{mapping.oci_service}</strong>
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            {mapping.oci_product}
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            {mapping.oci_product.includes('Data Science') ? 'Platform' :
                             mapping.oci_product.includes('Generative AI') ? 'Models' :
                             mapping.oci_product.includes('Vision') || mapping.oci_product.includes('Language') || mapping.oci_product.includes('Speech') ? 'API' :
                             'Service'}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            {mapping.oci_product.includes('Data Science') ? 'Per OCPU/hour' :
                             mapping.oci_product.includes('Generative AI') ? 'Per token' :
                             mapping.oci_product.includes('Vision') || mapping.oci_product.includes('Document') ? 'Per image/page' :
                             mapping.oci_product.includes('Language') ? 'Per character' :
                             mapping.oci_product.includes('Speech') ? 'Per minute' :
                             'Usage-based'}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>
                            <Chip 
                              label={`${Math.round((mapping.mapping_confidence || 0) * 100)}%`} 
                              size="small" 
                              color={mapping.mapping_confidence >= 0.9 ? 'success' : mapping.mapping_confidence >= 0.8 ? 'warning' : 'error'} 
                              variant="outlined" 
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                            {mapping.notes}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Storage Services */}
      {activeResearchTab === 'storage' && (
        <Box>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                Storage Services Mapping
                <Chip label={`${Object.keys(researchData.aws_to_oci_service_mappings?.storage_services || {}).length} Services`} size="small" color="primary" variant="outlined" />
              </Typography>
              
              {/* Storage Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'purple.50', borderRadius: 1, border: '1px solid', borderColor: 'purple.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'purple.dark', mb: 1 }}>
                  🗄️ v1.4 Expansion: Comprehensive Storage Services
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete AWS storage services mapped to OCI equivalents: S3, EBS, EFS, FSx and more.
                  Includes service mappings, pricing models, cost comparisons, and migration guidance.
                </Typography>
              </Box>

              {/* Service Mappings Table */}
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Service Name</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '18%' }}>Product</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Pricing Model</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Confidence</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '13%' }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(researchData.aws_to_oci_service_mappings?.storage_services || {}).map(([awsService, mapping]: [string, any]) => (
                      <React.Fragment key={awsService}>
                        <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                          <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                            <Chip label="AWS" color="warning" size="small" />
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                            <strong>{awsService}</strong>
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                            {awsService.includes('S3') || awsService.includes('Simple Storage') ? 'Object Storage' : 
                             awsService.includes('EBS') || awsService.includes('Block') ? 'Block Storage' :
                             awsService.includes('EFS') || awsService.includes('File System') ? 'File Storage' :
                             awsService.includes('FSx') ? 'Managed File System' :
                             awsService.includes('Glacier') ? 'Archive Storage' :
                             'Storage Service'}
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                            {awsService.includes('Glacier') ? 'Archive' : 
                             awsService.includes('EFS') || awsService.includes('FSx') ? 'File' :
                             awsService.includes('EBS') ? 'Block' : 
                             'Object'}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                            {awsService.includes('S3') || awsService.includes('Simple Storage') ? 'Per GB/month' : 
                             awsService.includes('EBS') || awsService.includes('Block') ? 'Per GB/month' :
                             awsService.includes('EFS') || awsService.includes('File System') ? 'Per GB/month' :
                             awsService.includes('FSx') ? 'Per GB/month + throughput' :
                             awsService.includes('Glacier') ? 'Per GB/month + retrieval' :
                             'Per GB/month'}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                            <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary', borderBottom: 'none' }}>
                            AWS storage service
                          </TableCell>
                        </TableRow>
                        <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                          <TableCell sx={{ py: 0.5 }}>
                            <Chip label="OCI" color="success" size="small" />
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                            <strong>{mapping.oci_service}</strong>
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            {mapping.oci_product}
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            {mapping.oci_product.includes('Archive') ? 'Archive' :
                             mapping.oci_product.includes('File System') ? 'File' :
                             mapping.oci_product.includes('Block') ? 'Block' :
                             'Object'}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            {mapping.oci_product.includes('Archive') ? 'Per GB/month + retrieval' :
                             mapping.oci_product.includes('File System') ? 'Per GB/month' :
                             mapping.oci_product.includes('Block') ? 'Per GB/month + IOPS' :
                             'Per GB/month'}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5 }}>
                            <Chip 
                              label={`${Math.round((mapping.mapping_confidence || 0) * 100)}%`} 
                              size="small" 
                              color={mapping.mapping_confidence >= 0.9 ? 'success' : mapping.mapping_confidence >= 0.8 ? 'warning' : 'error'} 
                              variant="outlined" 
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                            {mapping.notes}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pricing Comparison Table */}
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600, mt: 3, mb: 2 }}>
                💰 Pricing Comparison
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Storage Type</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Tier</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>$/GB/Month</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Requests</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '12%' }}>Transfer Out</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '9%' }}>Use Case</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Object Storage */}
                    <React.Fragment key="object-storage-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>S3 Standard</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Hot
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.023
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.0004/1K
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.09/GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Frequent
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Object Storage Standard</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Hot
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.0255
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.0004/1K
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.0085/GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Frequent
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="91% egress" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Archive Storage */}
                    <React.Fragment key="archive-storage-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Glacier Deep Archive</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Archive
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.00099
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.05/1K
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.09/GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Archive
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Archive Storage</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Archive
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.001
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.02/1K
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.0085/GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Archive
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="60% req" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Block Storage */}
                    <React.Fragment key="block-storage-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>EBS gp3</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          SSD
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.08
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          3K IOPS
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          125 MB/s
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          General
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Block Volume Balanced</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          SSD
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.0425
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          3K IOPS
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          240 MB/s
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          General
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="47%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Networking Services */}
      {activeResearchTab === 'networking' && (
        <Box>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                Networking & Data Transfer
                <Chip label="Ingress • Egress • IPs" size="small" color="info" variant="outlined" />
              </Typography>
              
              {/* Networking Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'indigo.50', borderRadius: 1, border: '1px solid', borderColor: 'indigo.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'indigo.dark', mb: 1 }}>
                  🌐 Network Infrastructure Costs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Compare data transfer costs, IP address pricing, and bandwidth charges for ingress/egress traffic.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Service</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Price</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Tier/Limit</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Use Case</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Data Transfer Out */}
                    <React.Fragment key="data-transfer-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Data Transfer Out</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Egress
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.09/GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          &gt;10TB/month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Internet
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Data Transfer Out</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Egress
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.0085/GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          &gt;10TB/month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Internet
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="91%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Public IP Addresses */}
                    <React.Fragment key="public-ip-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Elastic IP</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Static IP
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.005/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Unused
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Reserved
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Reserved Public IP</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Static IP
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.004/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Reserved
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Reserved
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="20%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Load Balancer */}
                    <React.Fragment key="load-balancer-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Application Load Balancer</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          L7 LB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.0225/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Base + LCU
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          HTTP/HTTPS
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Load Balancer</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          L7 LB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.0113/hr
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Base + GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          HTTP/HTTPS
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="50%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Route 53 DNS */}
                    <React.Fragment key="route53-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Route 53</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          DNS
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.50/hosted zone
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.40/1M queries
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Global DNS
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>DNS</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Managed DNS
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.40/hosted zone
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.30/1M queries
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Global DNS
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="25%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Security & Identity Services */}
      {activeResearchTab === 'security-identity' && (
        <Box>
          {/* Key Management & Encryption */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                Key Management & Encryption
                <Chip label="HSM • Auto-rotation • Compliance" size="small" color="warning" variant="outlined" />
              </Typography>
              
              {/* Key Management Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.dark', mb: 1 }}>
                  🔐 Enterprise Key Management & Encryption
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Centralized key management with hardware security modules (HSM), automatic rotation, and compliance controls for enterprise encryption needs.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '25%' }}>Service</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Category</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Pricing</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Features</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* KMS */}
                    <React.Fragment key="kms-security">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Key Management Service</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Encryption
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $1.00/key/month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          HSM, Rotation
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Vault</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Key Management
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.50/key/month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          HSM, Auto-rotation
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="50%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Cloud Security & Monitoring */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                Cloud Security & Monitoring
                <Chip label="SIEM • Threat Detection • Compliance" size="small" color="error" variant="outlined" />
              </Typography>
              
              {/* Security Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.dark', mb: 1 }}>
                  🛡️ Cloud-Native Security & Threat Detection
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Comprehensive security monitoring, threat detection, and compliance tools for cloud infrastructure protection and governance.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '25%' }}>Service</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Category</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Pricing</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Features</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* AWS GuardDuty vs OCI Cloud Guard */}
                    <React.Fragment key="threat-detection">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>GuardDuty</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Threat Detection
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $4.00/1M events
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          ML, DNS Analysis
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Cloud Guard</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Security Posture
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Free
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Activity Detector
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="100%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* AWS Security Hub vs OCI Security Zones */}
                    <React.Fragment key="security-hub">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Security Hub</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Security Management
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.0010/finding
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Compliance, Alerts
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Security Zones</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Secure Compartments
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Free
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Policy Enforcement
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="100%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Marketplace Security Solutions */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                Marketplace Security Solutions
                <Chip label="Third-Party • Enterprise • BYOL" size="small" color="info" variant="outlined" />
              </Typography>
              
              {/* Marketplace Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.dark', mb: 1 }}>
                  🏪 Top Enterprise Security Vendors
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Leading enterprise security solutions available in both AWS and OCI marketplaces with bring-your-own-license (BYOL) options.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Vendor</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Solution</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Category</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Pricing Model</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Availability</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Palo Alto Networks */}
                    <React.Fragment key="palo-alto">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Palo Alto Networks</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          VM-Series NGFW
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Firewall
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.15/hour + BYOL
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Available
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Palo Alto Networks</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          VM-Series NGFW
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Firewall
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.12/hour + BYOL
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Available
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="20%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* CrowdStrike */}
                    <React.Fragment key="crowdstrike">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>CrowdStrike</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Falcon Platform
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          EDR/XDR
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $8-15/endpoint/month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Available
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>CrowdStrike</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Falcon Platform
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          EDR/XDR
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $6-12/endpoint/month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Available
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="25%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Fortinet */}
                    <React.Fragment key="fortinet">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Fortinet</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          FortiGate NGFW
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Firewall
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.10/hour + BYOL
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Available
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Fortinet</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          FortiGate NGFW
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Firewall
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.08/hour + BYOL
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Available
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="20%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Check Point */}
                    <React.Fragment key="checkpoint">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Check Point</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          CloudGuard NGFW
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Firewall
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.12/hour + BYOL
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Available
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Check Point</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          CloudGuard NGFW
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Firewall
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.09/hour + BYOL
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Available
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="25%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Splunk */}
                    <React.Fragment key="splunk">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Splunk</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Enterprise Security
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          SIEM
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $1.50/GB/day
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Available
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Splunk</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Enterprise Security
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          SIEM
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $1.20/GB/day
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Available
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="20%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Other Services - Databases, Serverless, Monitoring */}
      {activeResearchTab === 'other-services' && (
        <Box>
          {/* Database Services */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                Database Services
                <Chip label="Managed • Serverless • Traditional" size="small" color="secondary" variant="outlined" />
              </Typography>
              
              {/* Database Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'secondary.50', borderRadius: 1, border: '1px solid', borderColor: 'secondary.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.dark', mb: 1 }}>
                  🗄️ Managed Database Solutions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fully managed database services including relational, NoSQL, and in-memory databases with automated backups, scaling, and security.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '25%' }}>Service</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Pricing</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Features</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* MySQL/PostgreSQL */}
                    <React.Fragment key="db-mysql-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>RDS MySQL</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Relational
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.017/hr (db.t3.micro)
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Multi-AZ, Backup
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>MySQL HeatWave</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          OLTP + Analytics
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.0125/hr (1 OCPU)
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Built-in Analytics
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="26%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Oracle Database */}
                    <React.Fragment key="db-oracle-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>RDS Oracle</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Enterprise
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $1.71/hr (db.t3.small)
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          BYOL Required
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Autonomous Database</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Serverless
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.73/hr (1 OCPU)
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Self-Managing
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="57%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* NoSQL */}
                    <React.Fragment key="db-nosql-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>DynamoDB</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          NoSQL
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.25/GB-month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Serverless
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>NoSQL Database</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Document/Key-Value
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.19/GB-month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Multi-Model
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="24%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Serverless Functions */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                Serverless Functions
                <Chip label="Event-Driven Computing" size="small" color="warning" variant="outlined" />
              </Typography>
              
              {/* Serverless Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.dark', mb: 1 }}>
                  ⚡ Function-as-a-Service (FaaS)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pay-per-execution serverless computing with automatic scaling and zero server management.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '25%' }}>Service</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Execution</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Duration</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Memory</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <React.Fragment key="lambda-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Lambda</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.20/1M requests
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.0000166667/GB-sec
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          128MB-10GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Functions</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.20/1M requests
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.0000133334/GB-sec
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          128MB-1GB
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="20%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Monitoring & Logging */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                Monitoring & Logging
                <Chip label="Observability" size="small" color="info" variant="outlined" />
              </Typography>
              
              {/* Monitoring Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.dark', mb: 1 }}>
                  📊 Application Performance Monitoring
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Comprehensive monitoring, logging, and alerting services for application performance and infrastructure health.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '25%' }}>Service</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '25%' }}>Pricing</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Features</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* CloudWatch vs OCI Monitoring */}
                    <React.Fragment key="monitoring-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>CloudWatch</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Monitoring
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.30/metric-month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Dashboards, Alarms
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Monitoring</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          APM
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.24/metric-month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Built-in Analytics
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="20%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* Logging */}
                    <React.Fragment key="logging-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>CloudWatch Logs</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Logging
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.50/GB ingested
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Log Insights
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Logging</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Centralized
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.39/GB ingested
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Log Analytics
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="22%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Application Services */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                Application Services
                <Chip label="API • Queues • Security" size="small" color="error" variant="outlined" />
              </Typography>
              
              {/* Application Services Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.dark', mb: 1 }}>
                  🔧 Application Integration & Security
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  API gateways, message queues, notification services, and security services for application integration and data protection.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '25%' }}>Service</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Pricing</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Features</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* API Gateway */}
                    <React.Fragment key="api-gateway-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>API Gateway</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          REST/HTTP
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $3.50/1M requests
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Throttling, Auth
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>API Gateway</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          REST/HTTP
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $1.00/1M requests
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Rate Limiting
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="71%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* SQS */}
                    <React.Fragment key="sqs-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Simple Queue Service</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Message Queue
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.40/1M requests
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          FIFO, DLQ
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Queue</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Message Queue
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.30/1M requests
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Dead Letter Queue
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="25%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* SNS */}
                    <React.Fragment key="sns-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Simple Notification Service</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Pub/Sub
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.50/1M publishes
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          SMS, Email, HTTP
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Notifications</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Event-driven
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.35/1M notifications
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Webhook, Email
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="30%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* CloudTrail */}
                    <React.Fragment key="cloudtrail-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>CloudTrail</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Audit Logging
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $2.00/100K events
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          API Calls, Console
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Audit</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Compliance Logs
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Included
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          All API Activity
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="100%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* DynamoDB */}
                    <React.Fragment key="dynamodb-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>DynamoDB</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          NoSQL
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.25/GB-month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Global Tables
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>NoSQL Database</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Document/Key-Value
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.19/GB-month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Multi-Model
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="24%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* ECR */}
                    <React.Fragment key="ecr-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Elastic Container Registry</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Container Registry
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.10/GB/month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Private, Scan
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Container Registry</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Docker Registry
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.05/GB/month
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Private, Security
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="50%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                    {/* EKS */}
                    <React.Fragment key="eks-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>Elastic Kubernetes Service</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          Managed K8s
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.10/hr/cluster
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Control Plane
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Container Engine for Kubernetes</strong>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Managed K8s
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          Free
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          No Control Plane Cost
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="100%" size="small" color="success" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* CDN & Edge Services - Enhanced */}
      {activeResearchTab === 'cdn-edge' && (
        <Box>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                CDN & Edge Services
                <Chip label="Global Distribution" size="small" color="success" variant="outlined" />
              </Typography>
              
              {/* CDN Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'teal.50', borderRadius: 1, border: '1px solid', borderColor: 'teal.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'teal.dark', mb: 1 }}>
                  🌍 Content Delivery & Edge Computing
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Global CDN pricing for data transfer, request handling, and edge computing services with built-in security features.
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Cloud</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '20%' }}>Service</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Edge Locations</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Data Transfer</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Requests</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '15%' }}>Features</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', fontWeight: 600, py: 1, width: '10%' }}>Savings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* CDN Services */}
                    <React.Fragment key="cdn-1">
                      <TableRow hover sx={{ bgcolor: 'warning.50', '&:hover': { bgcolor: 'warning.100' }, borderBottom: 'none' }}>
                        <TableCell sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Chip label="AWS" color="warning" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: 'none' }}>
                          <strong>CloudFront</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          400+
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.085/GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', borderBottom: 'none' }}>
                          $0.0075/10K
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem', borderBottom: 'none' }}>
                          Basic WAF
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, borderBottom: 'none' }}>
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.7rem' }}>↓</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' }, borderBottom: '2px solid rgba(25, 118, 210, 0.2)' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip label="OCI" color="success" size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          <strong>Web Application Acceleration</strong>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          44
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.0255/GB
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                          $0.004/10K
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                          Advanced WAF
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Chip label="70%" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>

                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}


      {activeResearchTab === 'tco' && (
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom>TCO Analysis</Typography>
            <Typography variant="body2" color="grey.600">
              Total cost of ownership analysis...
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Data Tab - JSON View & Download */}
      {activeResearchTab === 'data' && (
        <Box>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon sx={{ fontSize: '1.2rem' }} />
                  Research Data Export
                  <Chip label="JSON Format" size="small" color="primary" variant="outlined" />
                </Typography>
                <Tooltip title="Download complete dataset as JSON file">
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={downloadJsonData}
                    disabled={!researchData}
                    size="small"
                  >
                    Download JSON
                  </Button>
                </Tooltip>
              </Box>
              
              {/* Data Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.dark', mb: 1 }}>
                  📊 Complete Dataset Access
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and download the complete JSON dataset containing all AWS to OCI service mappings and pricing data used throughout this application.
                </Typography>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box>
                  {/* Research Data */}
                  {researchData && (
                    <Card elevation={1} sx={{ mb: 2 }}>
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                          🔍 Research Data
                          <Chip label={`${JSON.stringify(researchData).length} characters`} size="small" color="info" variant="outlined" />
                        </Typography>
                        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, overflow: 'auto', maxHeight: '400px' }}>
                          <pre style={{ margin: 0, fontSize: '0.75rem', lineHeight: 1.4 }}>
                            {JSON.stringify(researchData, null, 2)}
                          </pre>
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Data Export Info */}
                  <Card elevation={1} sx={{ bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.dark', mb: 1 }}>
                        💡 Export Information
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <strong>File Format:</strong> JSON with metadata, timestamps, and version info
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        • <strong>Contents:</strong> Complete SKU mappings and pricing data for all cloud services
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • <strong>Use Cases:</strong> Data analysis, integration, custom reporting, and research
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default Research;